import classNames from 'classnames';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, LabelS } from '@sorare/core/src/atoms/typography';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

const DECISIVE_SCORE_THRESHOLDS = [0, 5, 15, 35, 60, 70, 80, 90, 100];
const BASE_DESISIVE_SCORE = 35;
const BASE_DESICIVE_SCORE_INDEX = DECISIVE_SCORE_THRESHOLDS.findIndex(
  threshold => threshold === BASE_DESISIVE_SCORE
);

const steps = css`
  background-color: var(--c-surface-opaque);
  border-radius: var(--unit);
  width: 100%;
  flex: 1;
`;

const scoreGauge = css`
  position: absolute;
  top: calc(-1 * var(--intermediate-unit));
`;

const thresholdStep = css`
  padding: var(--double-and-a-half-unit) 0 var(--unit) var(--half-unit);
  align-self: stretch;
  opacity: 0.24;
  &.active {
    opacity: 1;
  }
  @media ${tabletAndAbove} {
    padding-left: var(--unit);
  }
  :first-child > & {
    border-top-left-radius: var(--unit);
    border-bottom-left-radius: var(--unit);
  }
  :last-child > & {
    border-top-right-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
`;

type Props = {
  score: number;
  sport: Sport;
};

const STEP_WIDTH = 100 / DECISIVE_SCORE_THRESHOLDS.length;

export const DecisiveScoreGauge = ({ score, sport }: Props) => {
  const activeThresholdIndex = DECISIVE_SCORE_THRESHOLDS.findIndex(
    (threshold, index) =>
      threshold <= score &&
      (index === DECISIVE_SCORE_THRESHOLDS.length - 1 ||
        score < DECISIVE_SCORE_THRESHOLDS[index + 1]) &&
      (index === 0 || score > DECISIVE_SCORE_THRESHOLDS[index - 1])
  );
  return (
    <div className="relative w-full">
      <Horizontal className={steps} gap={0}>
        {DECISIVE_SCORE_THRESHOLDS.map((threshold, index) => {
          const { color } = findThresholdColor(threshold, sport, 'CARD_SCORE');
          const active =
            threshold === DECISIVE_SCORE_THRESHOLDS[activeThresholdIndex];
          return (
            <Vertical key={threshold} center grow>
              <div
                style={{
                  background: color,
                }}
                className={classNames(thresholdStep, {
                  active,
                })}
              >
                <BrandXS color="var(--c-onBase-light)">{threshold}</BrandXS>
              </div>
              <LabelS color={active ? color : 'var(--c-nd-400)'} bold={active}>
                {index - BASE_DESICIVE_SCORE_INDEX}
              </LabelS>
            </Vertical>
          );
        })}
      </Horizontal>
      <div
        className={scoreGauge}
        style={{
          left: `${
            activeThresholdIndex * STEP_WIDTH +
            // position gauge on the right side of the threshold
            0.5 * STEP_WIDTH
          }%`,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="64"
          viewBox="0 0 16 64"
          fill="none"
        >
          <path
            d="M6.39194 11.3589L6.39194 56.5805C6.39194 57.3644 7.02749 58 7.81148 58C8.59547 58 9.23102 57.3645 9.23102 56.5805L9.23102 11.4032C9.23102 10.4277 9.58017 9.48434 10.2153 8.74389L10.6959 8.18357C12.7796 5.75443 11.0536 2 7.85323 2C4.70229 2 2.9597 5.654 4.94296 8.1025L5.46828 8.75106C6.06586 9.48883 6.39194 10.4094 6.39194 11.3589Z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );
};
